import { supabase } from './supabase';

/**
 * Which Ahrefs project a client's site is (clients.ahrefs_project_id). Pages sync, keyword sweeps and
 * everything downstream read that one column; nothing looks a project up by name or domain.
 */
export interface AhrefsProject {
  id: number;
  url: string;
  name: string;
}

/** Every project on the Ahrefs account. Free (no units), via netlify/functions/ahrefs-projects. */
export async function fetchAhrefsProjects(): Promise<AhrefsProject[]> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('not signed in');
  const res = await fetch('/.netlify/functions/ahrefs-projects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: '{}',
  });
  const text = await res.text();
  const body = (text ? JSON.parse(text) : {}) as { projects?: AhrefsProject[]; error?: string };
  if (!res.ok) throw new Error(body.error || text || `ahrefs-projects failed: ${res.status}`);
  return body.projects ?? [];
}

/** Which client each project is already linked to, by project id. */
export async function fetchLinkedProjects(): Promise<Map<number, { id: string; name: string }>> {
  const { data, error } = await supabase.from('clients').select('id, name, ahrefs_project_id').not('ahrefs_project_id', 'is', null);
  if (error) throw error;
  return new Map((data ?? []).map((c) => [Number(c.ahrefs_project_id), { id: c.id, name: c.name }]));
}

/**
 * Link (or unlink) the client's project. Clearing pages_sync_at puts the client at the front of the next
 * nightly pages sync, and the last attempt's error no longer applies to the new project.
 */
export async function setAhrefsProject(clientId: string, projectId: number | null): Promise<void> {
  const { error } = await supabase
    .from('clients')
    .update({ ahrefs_project_id: projectId, pages_sync_at: null, pages_sync_error: null })
    .eq('id', clientId);
  if (error) throw error;
}

/** "https://www.chartguys.com/" → "chartguys.com" */
export const projectHost = (url: string) => url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');

const squash = (s: string) => s.toLowerCase().replace(/^the\s+/, '').replace(/[^a-z0-9]/g, '');

/** Does the project look like this client's site? "The Chart Guys" ↔ chartguys.com. */
export function looksLike(project: AhrefsProject, clientName: string): boolean {
  const name = squash(clientName);
  if (name.length < 3) return false;
  const near = (s: string) => s.length >= 3 && (s.includes(name) || name.includes(s));
  return near(squash(projectHost(project.url).split('.')[0] ?? '')) || near(squash(project.name));
}
